import { ParentRelationshipType } from '../../api/api';
import { RelationSide, RelationTask } from './relationsGraph';

const RELATIONSHIP_LABELS: Record<ParentRelationshipType, string> = {
  [ParentRelationshipType.FollowUpToParent]: 'Opfølgning',
  [ParentRelationshipType.FollowUpSameWorkOrderAsParent]: 'Opfølgning',
  [ParentRelationshipType.PreparationForParent]: 'Forberedelse',
  [ParentRelationshipType.ExecutedWithParent]: 'Udføres sammen'
};

export const NODE_WIDTH = 230;
export const NODE_HEIGHT = 96;
export const PLACEHOLDER_HEIGHT = 48;

const CELL_GAP = 14;
const CLUSTER_GAP = 24;
const CLUSTER_PADDING_X = 14;
const CLUSTER_PADDING_TOP = 28;
const CLUSTER_PADDING_BOTTOM = 14;
const COLUMN_WIDTH = NODE_WIDTH + 2 * CLUSTER_PADDING_X;
const COLUMN_GAP = 96;
const HEADER_HEIGHT = 24;
const DIAGRAM_PADDING = 12;

const SIDES: RelationSide[] = ['preparation', 'visit', 'followUp'];

// The visit column has no header - the selected task's own cluster label already identifies it.
const COLUMN_HEADERS: Partial<Record<RelationSide, string>> = {
  preparation: 'FOREGÅENDE',
  followUp: 'EFTERFØLGENDE'
};

/**
 * Only a follow up task can be created from the diagram. A preparation task is not supported yet,
 * and a task in the same work order as the selected one is not allowed at all - add the side here
 * to offer it again.
 */
export const CREATABLE_SIDES: RelationSide[] = ['followUp'];

export interface DiagramNode {
  task: RelationTask;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DiagramCluster {
  key: string;
  label?: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DiagramConnector {
  key: string;
  path: string;
  side: RelationSide;
  /** Whether the arrowhead sits at the end of `path` (vs. the start) - see `addConnector`. */
  arrowAtEnd: boolean;
  /** Relationship type shown above the day label in the connector badge. */
  typeLabel?: string;
  label?: string;
  labelX: number;
  labelY: number;
  isPlaceholder: boolean;
}

export interface DiagramPlaceholder {
  key: string;
  side: RelationSide;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DiagramHeader {
  key: string;
  label: string;
  x: number;
  y: number;
}

export interface RelationsLayout {
  width: number;
  height: number;
  headers: DiagramHeader[];
  clusters: DiagramCluster[];
  nodes: DiagramNode[];
  connectors: DiagramConnector[];
  placeholders: DiagramPlaceholder[];
}

type Cell = { kind: 'task'; task: RelationTask } | { kind: 'placeholder'; side: RelationSide };

interface Cluster {
  key: string;
  label?: string;
  cells: Cell[];
}

const cellHeight = (cell: Cell) => (cell.kind === 'task' ? NODE_HEIGHT : PLACEHOLDER_HEIGHT);

const clusterContentHeight = (cluster: Cluster) =>
  cluster.cells.reduce((height, cell) => height + cellHeight(cell), 0) + (cluster.cells.length - 1) * CELL_GAP;

const isBoxed = (cluster: Cluster) => cluster.label !== undefined;

const clusterHeight = (cluster: Cluster) =>
  clusterContentHeight(cluster) + (isBoxed(cluster) ? CLUSTER_PADDING_TOP + CLUSTER_PADDING_BOTTOM : 0);

const columnX = (side: RelationSide) => DIAGRAM_PADDING + SIDES.indexOf(side) * (COLUMN_WIDTH + COLUMN_GAP);

const clusterLabel = (side: RelationSide, workOrderNumber: string | undefined, cellCount: number) => {
  if (workOrderNumber) return `BESØG ${workOrderNumber}`;
  // Tasks executed together belong to the same visit even before a work order number exists.
  if (side === 'visit' && cellCount > 1) return 'SAMME BESØG';
  return undefined;
};

/**
 * Groups the tasks of one column into clusters - one dashed box per work order number. The middle
 * column is always a single cluster: everything in it happens on the same visit as the selected
 * task, whether or not a work order number exists yet.
 */
const buildClusters = (side: RelationSide, tasks: RelationTask[], showPlaceholder: boolean): Cluster[] => {
  const clusters: Cluster[] = [];
  const clusterKey = (task: RelationTask) => {
    if (side === 'visit') return `${side}-cluster`;
    return task.workOrderNumber ? `${side}-wo-${task.workOrderNumber}` : `${side}-task-${task.id}`;
  };

  tasks.forEach((task) => {
    const existing = clusters.find((cluster) => cluster.key === clusterKey(task));

    if (existing) {
      existing.cells.push({ kind: 'task', task });
      return;
    }

    clusters.push({ key: clusterKey(task), cells: [{ kind: 'task', task }] });
  });

  if (showPlaceholder) {
    const placeholder: Cell = { kind: 'placeholder', side };
    const visitCluster = side === 'visit' ? clusters[0] : undefined;

    if (visitCluster) {
      visitCluster.cells.push(placeholder);
    } else {
      clusters.push({ key: `${side}-placeholder`, cells: [placeholder] });
    }
  }

  return clusters.map((cluster) => {
    const tasksInCluster = cluster.cells.reduce<RelationTask[]>(
      (found, cell) => (cell.kind === 'task' ? [...found, cell.task] : found),
      []
    );
    const workOrderNumber = (tasksInCluster.find((task) => task.isSelected) ?? tasksInCluster[0])?.workOrderNumber;

    return { ...cluster, label: clusterLabel(side, workOrderNumber, cluster.cells.length) };
  });
};

const elbowPath = (fromX: number, fromY: number, toX: number, toY: number) => {
  if (fromY === toY) return `M ${fromX} ${fromY} H ${toX}`;

  const midX = (fromX + toX) / 2;
  return `M ${fromX} ${fromY} H ${midX} V ${toY} H ${toX}`;
};

/**
 * Places the tasks in three columns and connects everything on the sides to the selected task
 * with a plain elbow line. Every column keeps its slot even when it is empty, so the diagram does
 * not jump around when the create placeholders appear.
 */
export const buildRelationsLayout = (tasks: RelationTask[], showPlaceholders = false): RelationsLayout => {
  const columns = SIDES.map((side) => ({
    side,
    clusters: buildClusters(
      side,
      tasks.filter((task) => task.side === side),
      showPlaceholders && CREATABLE_SIDES.includes(side)
    )
  }));

  const columnHeights = columns.map(
    (column) =>
      column.clusters.reduce((height, cluster) => height + clusterHeight(cluster), 0) +
      Math.max(column.clusters.length - 1, 0) * CLUSTER_GAP
  );
  const contentHeight = Math.max(NODE_HEIGHT, ...columnHeights);

  const clusters: DiagramCluster[] = [];
  const nodes: DiagramNode[] = [];
  const placeholders: DiagramPlaceholder[] = [];
  const clusterKeyByTaskId: Record<number, string> = {};

  columns.forEach((column, index) => {
    const x = columnX(column.side);
    let clusterY = DIAGRAM_PADDING + HEADER_HEIGHT + (contentHeight - columnHeights[index]) / 2;

    column.clusters.forEach((cluster) => {
      const boxed = isBoxed(cluster);
      const height = clusterHeight(cluster);

      clusters.push({
        key: cluster.key,
        label: cluster.label,
        x: boxed ? x : x + CLUSTER_PADDING_X,
        y: clusterY,
        width: boxed ? COLUMN_WIDTH : NODE_WIDTH,
        height
      });

      let cellY = clusterY + (boxed ? CLUSTER_PADDING_TOP : 0);
      cluster.cells.forEach((cell) => {
        if (cell.kind === 'task') {
          nodes.push({ task: cell.task, x: x + CLUSTER_PADDING_X, y: cellY, width: NODE_WIDTH, height: NODE_HEIGHT });
          clusterKeyByTaskId[cell.task.id] = cluster.key;
        } else {
          placeholders.push({
            key: `placeholder-${cell.side}`,
            side: cell.side,
            x: x + CLUSTER_PADDING_X,
            y: cellY,
            width: NODE_WIDTH,
            height: PLACEHOLDER_HEIGHT
          });
        }

        cellY += cellHeight(cell) + CELL_GAP;
      });

      clusterY += height + CLUSTER_GAP;
    });
  });

  const selectedNode = nodes.find((node) => node.task.isSelected);
  const selectedCluster = selectedNode
    ? clusters.find((cluster) => cluster.key === clusterKeyByTaskId[selectedNode.task.id])
    : undefined;
  const connectors: DiagramConnector[] = [];

  if (selectedNode && selectedCluster) {
    const anchorY = selectedNode.y + selectedNode.height / 2;
    // Anchor on the selected task itself, not its (possibly boxed) cluster - a connector never
    // targets a visit-column sibling, so the cluster's padding would only open a gap on this side.
    const leftAnchorX = selectedNode.x;
    const rightAnchorX = selectedNode.x + selectedNode.width;

    const addConnector = (
      key: string,
      side: RelationSide,
      bounds: { x: number; y: number; width: number; height: number },
      label: string | undefined,
      typeLabel: string | undefined,
      isPlaceholder: boolean,
      pointsToSelected: boolean
    ) => {
      const centerY = bounds.y + bounds.height / 2;
      // The preparation path always runs from the other node to the selected one, and the follow up
      // path always runs the other way. The arrow belongs at whichever end is the actual parent in
      // the relationship - usually the selected task, but not when the other node is its own parent.
      const path =
        side === 'preparation'
          ? elbowPath(bounds.x + bounds.width, centerY, leftAnchorX, anchorY)
          : elbowPath(rightAnchorX, anchorY, bounds.x, centerY);
      const arrowAtEnd = (side === 'preparation') === pointsToSelected;

      connectors.push({
        key,
        path,
        side,
        arrowAtEnd,
        typeLabel,
        label,
        labelX: side === 'preparation' ? (bounds.x + bounds.width + leftAnchorX) / 2 : (rightAnchorX + bounds.x) / 2,
        labelY: (anchorY + centerY) / 2,
        isPlaceholder
      });
    };

    nodes
      .filter((node) => !node.task.isSelected && clusterKeyByTaskId[node.task.id] !== selectedCluster.key)
      .forEach((node) =>
        addConnector(
          `connector-${node.task.id}`,
          node.task.side,
          node,
          node.task.dayLabel,
          node.task.relationshipType && RELATIONSHIP_LABELS[node.task.relationshipType],
          false,
          !node.task.isParentOfSelected
        )
      );

    // A placeholder always represents a task not yet created, which would be a child of the
    // selected task, so it always points at the selected task.
    placeholders
      .filter((placeholder) => placeholder.side !== 'visit')
      .forEach((placeholder) =>
        addConnector(`connector-${placeholder.key}`, placeholder.side, placeholder, undefined, undefined, true, true)
      );
  }

  const headers: DiagramHeader[] = columns
    .filter((column) => column.clusters.length > 0 && COLUMN_HEADERS[column.side])
    .map((column) => ({
      key: `header-${column.side}`,
      label: COLUMN_HEADERS[column.side] as string,
      x: columnX(column.side) + COLUMN_WIDTH / 2,
      y: DIAGRAM_PADDING + HEADER_HEIGHT / 2
    }));

  return {
    width: DIAGRAM_PADDING * 2 + SIDES.length * COLUMN_WIDTH + (SIDES.length - 1) * COLUMN_GAP,
    height: DIAGRAM_PADDING * 2 + HEADER_HEIGHT + contentHeight,
    headers,
    clusters,
    nodes,
    connectors,
    placeholders
  };
};
